package com.exura.pages.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.components.layouts.plan.LayoutMode
import com.exura.features.firebase.domain.model.versioning.Version
import com.exura.features.plandefinition.domain.model.metadata.PlanDefinitionMetadata
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.usecase.DeleteTreatmentPlanUseCase
import com.exura.features.plandefinition.usecase.ListTreatmentPlansUseCase
import com.exura.navigation.Route
import com.exura.navigation.navigateTo
import com.exura.pages.planCreator.components.ViewMode
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.core.PageContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

data class TemplateDashboardUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val plans: List<Version<BasePlan, PlanDefinitionMetadata>> = emptyList()
)

sealed interface TemplateDashboardEvent {
    data object RefreshPlans : TemplateDashboardEvent
    data class DeletePlan(val planId: String) : TemplateDashboardEvent
    data class PreviewPlan(val planId: String) : TemplateDashboardEvent
    data class EditPlan(val planId: String) : TemplateDashboardEvent
    data class TransferPlan(val planId: String) : TemplateDashboardEvent
}

class TemplateDashboardController(
    private val scope: CoroutineScope,
    private val context: PageContext,
    private val listTreatmentPlansUseCase: ListTreatmentPlansUseCase,
    private val deleteTreatmentPlanUseCase: DeleteTreatmentPlanUseCase,
) {
    var uiState by mutableStateOf(TemplateDashboardUiState())
        private set

    private var loadJob: Job? = null

    init {
        loadPlans()
    }

    fun loadPlans() {
        loadJob?.cancel()

        loadJob = scope.launch {
            uiState = uiState.copy(isLoading = true, error = null)

            listTreatmentPlansUseCase()
                .onSuccess { flow ->
                    flow.collect { plans ->
                        uiState = uiState.copy(
                            isLoading = false,
                            plans = plans
                        )
                    }
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isLoading = false,
                        error = error.message
                    )
                }
        }
    }

    suspend fun handleEvent(event: TemplateDashboardEvent) {
        when (event) {
            is TemplateDashboardEvent.DeletePlan -> deletePlan(event.planId)
            is TemplateDashboardEvent.PreviewPlan -> navigateToPlanPreview(event.planId)
            is TemplateDashboardEvent.EditPlan -> navigateToPlanEdit(event.planId)
            is TemplateDashboardEvent.TransferPlan -> navigateToPlanTransfer(event.planId)
            is TemplateDashboardEvent.RefreshPlans -> loadPlans()
        }
    }

    private suspend fun deletePlan(planId: String) {
            uiState = uiState.copy(isLoading = true)
            deleteTreatmentPlanUseCase(planId)
                .onSuccess {
                    uiState = uiState.copy(isLoading = false)
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isLoading = false,
                        error = error.message
                    )
                }
    }

    private fun navigateToPlanPreview(planId: String) {
        context.navigateTo(
            Route.WithParams.toPlanPreview(planId = planId)
        )
    }

    private fun navigateToPlanEdit(planId: String) {
        context.navigateTo(
            Route.WithParams.toPlanPreview(planId = planId, layoutMode = LayoutMode.EDIT)
        )
    }

    private fun navigateToPlanTransfer(planId: String) {
        context.navigateTo(Route.WithParams.toPlanDistribution(planId))
    }
}

@Composable
fun rememberTemplateDashboardController(
    scope: CoroutineScope,
    context: PageContext,
): TemplateDashboardController {
    val listTreatmentPlansUseCase = injectAndRemember<ListTreatmentPlansUseCase>()
    val deleteTreatmentPlanUseCase = injectAndRemember<DeleteTreatmentPlanUseCase>()

    return remember(scope, context) {
        TemplateDashboardController(
            scope = scope,
            context = context,
            listTreatmentPlansUseCase = listTreatmentPlansUseCase,
            deleteTreatmentPlanUseCase = deleteTreatmentPlanUseCase,
        )
    }
}
